import React from 'react';

const getBirthYear = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.getFullYear();
};

const getUniqueVilles = (submissions) => {
  const villes = submissions
    .map(s => s.ville)
    .filter(v => v && v.trim() !== '');
  return [...new Set(villes)].sort();
};

const getUniqueQuartiers = (submissions) => {
  const quartiers = submissions
    .map(s => s.quartier)
    .filter(q => q && q.trim() !== '');
  return [...new Set(quartiers)].sort();
};

const getAvailableYears = (submissions) => {
  const years = submissions
    .map(s => getBirthYear(s.dateNaissance))
    .filter(y => y !== null);
  return [...new Set(years)].sort((a, b) => b - a);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Non spécifié';
  const date = dateString instanceof Date ? dateString : new Date(dateString);
  if (isNaN(date.getTime())) return 'Date invalide';
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const getPostesSouhaites = (postes) => {
  if (!postes) return 'Aucun';
  const selected = [];
  if (postes.plomberie) selected.push('Plomberie');
  if (postes.peinture) selected.push('Peinture');
  if (postes.electricite) selected.push('Électricité');
  if (postes.froidClimatisation) selected.push('Froid/Climatisation');
  if (postes.autre) selected.push('Autre');
  return selected.length > 0 ? selected.join(', ') : 'Aucun';
};

const formatDisponibilitePreference = (pref) => {
  let prefObj = pref;
  if (typeof pref === 'string') {
    try {
      prefObj = JSON.parse(pref);
    } catch (e) {
      return pref || 'N/A';
    }
  }

  if (!prefObj || (typeof prefObj === 'object' && Object.keys(prefObj).length === 0)) {
    return 'N/A';
  }

  const preferences = [];

  const joursOuvrables = prefObj.joursOuvrables;
  if (joursOuvrables === true || joursOuvrables === 'true' || joursOuvrables === '1' || joursOuvrables === 1) {
    preferences.push('Jours ouvrables');
  }

  const soirs = prefObj.soirs;
  if (soirs === true || soirs === 'true' || soirs === '1' || soirs === 1) {
    preferences.push('Soirs');
  }

  const weekend = prefObj.weekend;
  if (weekend === true || weekend === 'true' || weekend === '1' || weekend === 1) {
    preferences.push('Weekend');
  }

  if (preferences.length === 0) {
    const hasJoursOuvrables = prefObj.lundiMatin || prefObj.lundiApresMidi ||
                             prefObj.mardiMatin || prefObj.mardiApresMidi ||
                             prefObj.mercrediMatin || prefObj.mercrediApresMidi ||
                             prefObj.jeudiMatin || prefObj.jeudiApresMidi ||
                             prefObj.vendrediMatin || prefObj.vendrediApresMidi;
    const hasWeekend = prefObj.samediMatin || prefObj.samediApresMidi ||
                       prefObj.dimancheMatin || prefObj.dimancheApresMidi;

    if (hasJoursOuvrables) preferences.push('Jours ouvrables');
    if (hasWeekend) preferences.push('Weekend');
  }

  return preferences.length > 0 ? preferences.join(', ') : 'N/A';
};


function CandidaturesPanel({
  submissions,
  filteredSubmissions,
  filter,
  setFilter,
  filterYear,
  setFilterYear,
  filterVille,
  setFilterVille,
  filterQuartier,
  setFilterQuartier,
  onEdit,
  onDelete,
}) {
  return (
    <>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-center mb-4 pb-2 border-b-2 border-blue-400">
                <h3 className="text-lg font-semibold text-gray-800">
                  Filtres de Recherche
                </h3>
                <span className="text-sm font-medium text-gray-600">
                  Total : <span className="text-blue-600 font-bold">{filteredSubmissions.length}</span> candidature{filteredSubmissions.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Filtrer par Poste :</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filter === 'all'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Tous ({submissions.length})
                  </button>
                  <button
                    onClick={() => setFilter('plomberie')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filter === 'plomberie'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Plomberie ({submissions.filter(s => s.posteSouhaite?.plomberie).length})
                  </button>
                  <button
                    onClick={() => setFilter('peinture')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filter === 'peinture'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Peinture ({submissions.filter(s => s.posteSouhaite?.peinture).length})
                  </button>
                  <button
                    onClick={() => setFilter('electricite')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filter === 'electricite'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Électricité ({submissions.filter(s => s.posteSouhaite?.electricite).length})
                  </button>
                  <button
                    onClick={() => setFilter('froidClimatisation')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filter === 'froidClimatisation'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Froid/Climatisation ({submissions.filter(s => s.posteSouhaite?.froidClimatisation).length})
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filtrer par Ville :
                  </label>
                  <select
                    value={filterVille}
                    onChange={(e) => setFilterVille(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                  >
                    <option value="">Toutes les villes</option>
                    {getUniqueVilles(submissions).map((ville) => (
                      <option key={ville} value={ville}>
                        {ville} ({submissions.filter(s => s.ville === ville).length})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filtrer par Quartier :
                  </label>
                  <select
                    value={filterQuartier}
                    onChange={(e) => setFilterQuartier(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                  >
                    <option value="">Tous les quartiers</option>
                    {getUniqueQuartiers(submissions).map((quartier) => (
                      <option key={quartier} value={quartier}>
                        {quartier} ({submissions.filter(s => s.quartier === quartier).length})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filtrer par Année de Naissance :
                  </label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                  >
                    <option value="">Toutes les années</option>
                    {getAvailableYears(submissions).map((year) => (
                      <option key={year} value={year}>
                        {year} ({submissions.filter(s => getBirthYear(s.dateNaissance) === year).length})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {filteredSubmissions.length === 0 ? (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-12 text-center">
                <p className="text-gray-500 text-xl">Aucune candidature trouvée</p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredSubmissions.map((submission) => (
                  <div
                    key={submission._id || submission.id}
                    className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 transition-all duration-200 hover:shadow-xl"
                  >
                    <div className="flex justify-between items-start mb-6 pb-4 border-b-2 border-blue-400">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800">
                          {submission.prenom} {submission.nom}
                        </h3>
                        <p className="text-gray-600 mt-1">
                          Candidature soumise le {formatDate(submission.dateSoumission || submission.submittedAt || submission.createdAt)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => onEdit(submission)}
                          className="text-blue-600 hover:text-blue-800 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => onDelete(submission._id || submission.id)}
                          className="text-red-500 hover:text-red-700 font-medium px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-blue-400">
                          Informations Personnelles
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <p className="text-gray-600">Nom</p>
                            <p className="font-medium text-gray-800">{submission.nom}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Prénom</p>
                            <p className="font-medium text-gray-800">{submission.prenom}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Téléphone</p>
                            <p className="font-medium text-gray-800">{submission.telephone}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Date de naissance</p>
                            <p className="font-medium text-gray-800">{formatDate(submission.dateNaissance)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Ville</p>
                            <p className="font-medium text-gray-800">{submission.ville || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Quartier</p>
                            <p className="font-medium text-gray-800">{submission.quartier || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-blue-400">
                          Poste & Formation
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <p className="text-gray-600">Poste souhaité</p>
                            <p className="font-medium text-gray-800">{getPostesSouhaites(submission.posteSouhaite)}</p>
                            {submission.posteSouhaite?.autre && submission.autreDomaine && (
                              <p className="text-xs text-gray-500 mt-1">Autre: {submission.autreDomaine}</p>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-600">Niveau de scolarisation</p>
                            <p className="font-medium text-gray-800">{submission.niveauScolarisation || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Formation professionnelle</p>
                            <p className="font-medium text-gray-800">{submission.formationProfessionnelle || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Années d'expérience</p>
                            <p className="font-medium text-gray-800">{submission.anneesExperience || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Niveau de compétence</p>
                            <p className="font-medium text-gray-800">{submission.niveauCompetence || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-blue-400">
                          Compétences & Disponibilité
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <p className="text-gray-600">Lecture de plans</p>
                            <p className="font-medium text-gray-800">{submission.lecturePlans || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Capacité instructions</p>
                            <p className="font-medium text-gray-800">{submission.capaciteInstructions || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Matériel de travail</p>
                            <p className="font-medium text-gray-800">{submission.materielTravail || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Disponibilité (jours)</p>
                            <p className="font-medium text-gray-800">{submission.disponibiliteCommencer || submission.disponibiliteJours || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Smartphone</p>
                            <p className="font-medium text-gray-800">{submission.smartphone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Internet Mobile</p>
                            <p className="font-medium text-gray-800">{submission.accesInternetMobile || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Moyen de déplacement</p>
                            <p className="font-medium text-gray-800">
                              {submission.moyenDeplacement || 'N/A'}
                              {submission.moyenDeplacement === 'autre' && submission.autreMoyenDeplacement && (
                                <span className="text-gray-500"> ({submission.autreMoyenDeplacement})</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Préférence disponibilité</p>
                            <p className="font-medium text-gray-800">
                              {formatDisponibilitePreference(submission.disponibilitePreference)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
    </>
  );
}

export default CandidaturesPanel;
